import React, { memo, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import type { RequestTypeCell$key } from '@atlassian/jira-relay/src/__generated__/RequestTypeCell.graphql';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';

export type Props = {
	fieldRef: RequestTypeCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};

const REQUEST_TYPE_IMAGE_SIZE = 16;

export const RequestTypeCell = memo<Props>(({ fieldRef, isReadOnlyWithoutHoverPopover }) => {
	const data = useFragment<RequestTypeCell$key>(
		graphql`
			fragment RequestTypeCell on JiraServiceManagementRequestTypeField {
				id
				requestType {
					name
					avatar {
						xsmall
					}
				}
			}
		`,
		fieldRef,
	);

	const { requestType } = data;

	const hoverPopoverEnabled = useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

	const contentRef = useRef(null);
	const hoverPopoverRefFactory = hoverPopoverEnabled
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks, jira/react/no-usage-of-hooks-outside-components
			useHoverPopoverContentRef(data.id)
		: null;

	if (!requestType) {
		return null;
	}

	if (!requestType.name) {
		return <ErrorCell cellType="requestType" reason="Missing required fields" />;
	}

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: requestType.name,
		});
	}

	const content = (ref: React.Ref<HTMLDivElement> | null = null) => (
		<Inline alignBlock="center" space="space.100" ref={ref}>
			{requestType?.avatar?.xsmall && (
				<Box xcss={imageContainerStyles}>
					<img src={requestType.avatar.xsmall} width={REQUEST_TYPE_IMAGE_SIZE} alt="" />
				</Box>
			)}

			<Box
				as="span"
				xcss={hoverPopoverEnabled ? textEllipsesStyles : textOverflowStyles}
				ref={contentRef}
			>
				{requestType.name}
			</Box>
		</Inline>
	);

	if (hoverPopoverEnabled) {
		return content(hoverPopoverRef);
	}

	return content();
});

const imageContainerStyles = xcss({
	display: 'inline',
	height: `${REQUEST_TYPE_IMAGE_SIZE}px`,
});

const textOverflowStyles = xcss({
	display: '-webkit-box',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 2,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	wordBreak: 'break-word',
});

const textEllipsesStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	wordBreak: 'break-word',
});
